import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

// Constants for better maintainability
const FILE_TYPES = {
  AIR: 'air',
  ITINERARY: 'itinerary',
  BOARDING_PASS: 'boarding_pass',
  ENTRY_STAMP: 'entry_stamp',
  ARN: 'arn',
  GIVEBACK_APPLICATION: 'giveback_application'
}

const OCR_FIELDS = {
  BOARDING_FIRST_NAME: 'boarding_first_name',
  BOARDING_LAST_NAME: 'boarding_last_name',
  BOARDING_AIRLINE_NAME: 'boarding_airline_name',
  BOARDING_FLIGHT_NUMBER: 'boarding_flight_number',
  BOARDING_EXIT_POINT: 'boarding_exit_point',
  BOARDING_DATE_OF_DEPARTURE: 'boarding_date_of_departure'
}

const VERIFY_FIELDS = {
  DATE_OF_VISIT: 'date_of_visit',
  BOARDING_FIRST_NAME: 'boarding_first_name',
  BOARDING_LAST_NAME: 'boarding_last_name',
  BOARDING_AIRLINE_NAME: 'boarding_airline_name',
  BOARDING_FLIGHT_NUMBER: 'boarding_flight_number',
  BOARDING_EXIT_POINT: 'boarding_exit_point',
  BOARDING_DATE_OF_DEPARTURE: 'boarding_date_of_departure'
}

// Helper functions
const createOcrField = () => ({
  ocr: 0,
  score: '',
  manual: 0
})

const createVerifyField = () => false

const createOcrObject = () => {
  const ocr = {}
  Object.values(OCR_FIELDS).forEach(field => {
    ocr[field] = createOcrField()
  })
  return ocr
}

const createVerifyObject = () => {
  const verify = {}
  Object.values(VERIFY_FIELDS).forEach(field => {
    verify[field] = createVerifyField()
  })
  return verify
}

const createFilesObject = () => ({
  [FILE_TYPES.AIR]: [],
  [FILE_TYPES.ITINERARY]: [],
  [FILE_TYPES.BOARDING_PASS]: [],
  [FILE_TYPES.ENTRY_STAMP]: [],
  [FILE_TYPES.ARN]: [],
  [FILE_TYPES.GIVEBACK_APPLICATION]: []
})

// Initial state
const initialState = {
  attributes: {
    // first_name: null,
    // last_name: null,
    // nationality: null,
    // gender: null,
    // airline_name: null,
    // flight_number: null,
    entry_stamp: '0',
    boarding_verified: '0',
    itinerary_clicked: false,
    arn_clicked: false,
    ocr: createOcrObject(),
    verify: createVerifyObject(),
    // Additional trip attributes from API
    id: '',
    name: 'Trip',
    date_entered: '',
    date_modified: '',
    modified_user_id: null,
    created_by: '',
    description: null,
    deleted: '0',
    assigned_user_id: '',
    cruise_name: null,
    date_of_visit: null,
    transport_number: null,
    route_of_stay: null,
    transport_company: null,
    way_of_transport: null,
    code: null,
    town: null,
    exit_point: null,
    way_of_exit: null,
    ship_name: null,
    contact_id: '',
    date_of_birth: null,
    type_of_trip: null,
    legible_entry_stamp: '0',
    signature_like_passport: '0',
    passport_number: null,
    air_sea_type: '',
    send_secure_link: '0',
    send_itinerary_secure_link: '0',
    cruise_itinerary_secure_link: '0',
    send_cruise_id_secure_link: '0',
    cruise_port: null,
    boarding_first_name: null,
    boarding_last_name: null,
    boarding_airline_name: null,
    boarding_flight_number: null,
    boarding_exit_point: null,
    boarding_date_of_departure: null,
    send_fmm_secure_link: '0',
    send_entry_stamp_secure_link: '0',
    boarding_verified: '0',
    send_cruise_schedule_secure_link: '0'
  },
  files: createFilesObject(),
  metadata: {},
  airlines: [],
  cruises: []
}

export const tripAirSlice = createSlice({
  name: 'trip_air',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    setTripAir: (state, action) => {
      state.attributes = { ...state.attributes, ...action.payload }
    },

    setTripAirFromAPI: (state, action) => {
      // Debug: Check action payload
      const tripData = action.payload
      const { attributes, files, airlines, cruises } = tripData

      // Update attributes
      if (attributes) {
        // Debug: Check attributes being updated
        state.attributes = {
          ...state.attributes,
          ...attributes
        }
      }

      // Update files if provided
      if (files) {
        state.files = {
          ...state.files,
          ...files
        }
      }

      // Update airlines and cruises lists
      if (airlines) {
        state.airlines = airlines
      }
      if (cruises) {
        state.cruises = cruises
      }
    },

    addTripFile: (state, action) => {
      const { file_type } = action.payload
      if (state.files[file_type]) {
        state.files[file_type].push({})
      }
    },

    removeTripFile: (state, action) => {
      const { file_type, file_no } = action.payload
      if (state.files[file_type] && Array.isArray(state.files[file_type])) {
        state.files[file_type].splice(file_no, 1)
      }
    },

    setTripFile: (state, action) => {
      const { file_type, file_no, file_obj, base64 } = action.payload
      if (
        state.files[file_type] &&
        state.files[file_type][file_no] !== undefined
      ) {
        state.files[file_type][file_no] = {
          file_obj,
          base64
        }
      }
    },

    setTripFileID: (state, action) => {
      const { file_type, file_no, id, document_revision_id } = action.payload
      if (state.files[file_type] && state.files[file_type][file_no]) {
        state.files[file_type][file_no].id = id
        if (document_revision_id) {
          state.files[file_type][file_no].file_obj.document_revision_id =
            document_revision_id
        }
      }
    },

    setTripArnFiles: (state, action) => {
      state.files.arn = action.payload.files
    },

    setAllAirTripFiles: (state, action) => {
      const { file_type, files } = action.payload
      if (state.files[file_type] !== undefined) {
        state.files[file_type] = files
      }
    },

    setMetaData: (state, action) => {
      state.metadata = { ...state.metadata, ...action.payload }
    },

    initializeTripAir: state => {
      return initialState
    },

    setAllTripFiles: (state, action) => {
      const { file_type, files } = action.payload
      if (state.files[file_type] !== undefined) {
        state.files[file_type] = files
      }
    },

    addTripAirFileWithData: (state, action) => {
      const { file_type, file } = action.payload
      if (state.files[file_type]) {
        // Always add the file, don't check length
        state.files[file_type].push(file)
      }
    },

    createNewTripAir: state => {
      return {
        ...state,
        attributes: {
          ...initialState.attributes,
          air_sea_type: 'air'
        },
        files: {
          [FILE_TYPES.AIR]: [],
          [FILE_TYPES.ITINERARY]: [],
          [FILE_TYPES.BOARDING_PASS]: [],
          [FILE_TYPES.ENTRY_STAMP]: [],
          [FILE_TYPES.ARN]: [],
          [FILE_TYPES.GIVEBACK_APPLICATION]: []
        },
        metadata: {}
      }
    },

    boardingVerifyAll: (state, action) => {
      const { verify } = state.attributes

      const fieldsToUpdate = Object.keys(verify).filter(
        key => key !== VERIFY_FIELDS.DATE_OF_VISIT
      )

      fieldsToUpdate.forEach(field => {
        verify[field] = action.payload.value
      })
    },

    setTripsList: (state, action) => {
      const { airlines_list, cruises_list } = action.payload
      state.airlines = airlines_list
      state.cruises = cruises_list
    },
    setSingleInputFeild: (state, action) => {
      const { field, value } = action.payload
      state.attributes[field] = value
    },
    setSingleVerifyField: (state, action) => {
      const { field, value } = action.payload
      state.attributes.verify[field] = value
    },
    updateTripAirOcrField: (state, action) => {
      const { field, data } = action.payload
      state.attributes.ocr[field] = {
        ...state.attributes.ocr[field],
        ...data
      }
    },
    setTripAirFromAPICustomerModal: (state, action) => {
      const tripAirData = action.payload
      if (tripAirData) {
        state.attributes = { ...state.attributes, ...tripAirData }
      }
    }
  }
})

export const {
  setTripAir,
  setTripAirFromAPI,
  addTripFile,
  boardingVerifyAll,
  removeTripFile,
  setTripFile,
  setTripFileID,
  addTripAirFileWithData,
  setMetaData,
  initializeTripAir,
  setAllTripFiles,
  setTripArnFiles,
  setAllAirTripFiles,
  createNewTripAir,
  setTripsList,
  setSingleInputFeild,
  setSingleVerifyField,
  updateTripAirOcrField,
  setTripAirFromAPICustomerModal
} = tripAirSlice.actions

export const selectTripAir = state => state?.tripAir
export const selectBoardingPassFiles = state =>
  state?.tripAir?.files?.boarding_pass || []
export const selectEntryStampFiles = state =>
  state?.tripAir?.files?.entry_stamp || []
export const selectAirlineFiles = state => state?.tripAir?.files?.air || []
export const selectItineraryFiles = state =>
  state?.tripAir?.files?.itinerary || []
export const selectArnFiles = state => state?.tripAir?.files?.arn || []

export default tripAirSlice.reducer
